import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Loader2, LogOut, MessageCircle, Pencil, RefreshCw, Search, Star } from "lucide-react";
import { toast } from "sonner";
import { Brand } from "../../components/Navbar";
import ChangePassword from "../../components/ChangePassword";
import { StatusBadge } from "../TrackPage";
import { useAuth, ADMIN_ROLES } from "../../context/AuthContext";
import api from "../../lib/api";
import { dialCodeFor } from "../../lib/countryDialCodes";

const CCARE_ROLES = ["ccare", ...ADMIN_ROLES];

const GOOGLE_REVIEW_URL = "https://reviewthis.biz/indiafast";

const inputCls = "rounded-lg border border-stone-300 bg-white px-3.5 py-2.5 text-sm text-navy outline-none focus:border-saffron";

const CARRIER_LINKS = [
  [/dhl/i, "https://www.dhl.com/in-en/home/tracking.html?tracking-id={tn}"],
  [/fedex/i, "https://www.fedex.com/fedextrack/?trknbr={tn}"],
  [/ups/i, "https://www.ups.com/track?tracknum={tn}"],
  [/delhivery/i, "https://www.delhivery.com/track/package/{tn}"],
  [/bluedart|blue dart/i, "https://www.bluedart.com/web/guest/trackdartresult?trackFor=0&trackingNo={tn}"],
  [/dtdc/i, "https://www.dtdc.in/tracking/tracking_results.asp?Ttype=awb_no&strCnno={tn}"],
  [/aramex/i, "https://www.aramex.com/in/en/track/shipments?ShipmentNumber={tn}"],
  [/shadowfax/i, "https://www.shadowfax.in/track?orderId={tn}"],
  [/xpressbees/i, "https://www.xpressbees.com/shipment/tracking?t={tn}"],
  [/ecom/i, "https://ecomexpress.in/tracking/?awb_field={tn}"],
  [/dpex|tpc/i, "https://www.dtdc.com/track"],
];

export function carrierTrackUrl(s) {
  const tn = s.tracking_number || s.awb;
  if (!tn) return null;
  for (const [re, tpl] of CARRIER_LINKS) {
    if (re.test(s.courier_name || "") || re.test(s.service_name || "")) return tpl.replace("{tn}", tn);
  }
  return null;
}

export function waMsg(s, who) {
  const p = who === "sender" ? s.pickup : s.delivery;
  const last = (s.tracking || []).slice(-1)[0] || {};
  const origin = s.pickup?.city || s.origin_city || "India";
  const dest = s.delivery?.city || s.destination_city || s.destination_country || "";
  const lines = [
    `Namaste ${p?.name || "Customer"}!`,
    ``,
    `This is INDIAFAST Customer Care. Welcome aboard — your shipment is in safe hands with us.`,
    ``,
    `AWB: ${s.awb}`,
    `Status: ${s.status}`,
  ];
  if (last.status) lines.push(`Last update: ${last.status}${last.location ? ` — ${last.location}` : ""}`);
  lines.push(`Route: ${origin} → ${dest}`);
  if (s.expected_delivery) lines.push(`Expected delivery: ${s.expected_delivery}`);
  lines.push(``, `Track live: https://www.indiafast.in/track/${s.awb}`, `Need help? Reply here or call +91 97838 07838.`);
  return `https://wa.me/${waPhoneFor(s, who)}?text=${encodeURIComponent(lines.join("\n"))}`;
}

export function ratingMsg(s, who) {
  const p = who === "sender" ? s.pickup : s.delivery;
  const lines = [
    `Namaste ${p?.name || "Customer"}!`,
    ``,
    `Thank you for shipping with INDIAFAST Global Couriers.`,
    `Your shipment ${s.awb} is currently: ${s.status}.`,
    ``,
    `Loved our service? Please rate us on Google — it takes just 30 seconds:`,
    GOOGLE_REVIEW_URL,
    ``,
    `Your feedback helps us serve you better. — Team INDIAFAST`,
  ];
  return `https://wa.me/${waPhoneFor(s, who)}?text=${encodeURIComponent(lines.join("\n"))}`;
}

export function waPhoneFor(s, who) {
  const p = who === "sender" ? s.pickup : s.delivery;
  const digits = String(p?.phone || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length > 10) return digits; // already carries its own country code
  // derive prefix from the shipment's country data
  const country = who === "receiver" ? (s.delivery?.country || s.destination_country) : "India";
  return `${dialCodeFor(country) || 91}${digits}`;
}

export function displayPhone(s, who) {
  const p = who === "sender" ? s.pickup : s.delivery;
  const digits = String(p?.phone || "").replace(/\D/g, "");
  if (!digits) return "—";
  if (digits.length > 10) return `+${digits}`;
  const country = who === "receiver" ? (s.delivery?.country || s.destination_country) : "India";
  const dial = dialCodeFor(country);
  return dial ? `+${dial} ${digits}` : digits;
}

export default function CCare() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState("");
  const [phoneEdit, setPhoneEdit] = useState(null);
  const [ratingFor, setRatingFor] = useState(null);
  const [phoneForm, setPhoneForm] = useState({ pickup_phone: "", delivery_phone: "" });
  const [savingPhone, setSavingPhone] = useState(false);

  const openPhoneEdit = (s) => {
    setPhoneEdit(s);
    setPhoneForm({ pickup_phone: s.pickup?.phone || "", delivery_phone: s.delivery?.phone || "" });
  };

  const savePhones = async (e) => {
    e.preventDefault();
    if (savingPhone) return;
    setSavingPhone(true);
    try {
      await api.patch(`/ccare/shipments/${phoneEdit.id}/phones`, phoneForm);
      toast.success(`Contact numbers updated for ${phoneEdit.awb}`);
      setPhoneEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update numbers");
    } finally { setSavingPhone(false); }
  };

  useEffect(() => {
    if (!authLoading && (!user || !CCARE_ROLES.includes(user.role))) navigate("/login");
  }, [user, authLoading, navigate]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 50 });
    if (q.trim()) params.set("q", q.trim());
    if (status) params.set("status", status);
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    api.get(`/ccare/shipments?${params}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load shipments")).finally(() => setLoading(false));
  }, [q, status, dateFrom, dateTo, page]);

  useEffect(() => { load(); }, [load]);

  const refreshLive = async (s) => {
    setRefreshing(s.awb);
    try {
      const res = await api.get(`/public/track/${s.awb}`);
      setData((d) => ({ ...d, items: d.items.map((x) => x.id === s.id ? { ...x, status: res.data.status || x.status, tracking: res.data.tracking || x.tracking } : x) }));
      toast.success(`${s.awb}: ${res.data.status}`);
    } catch { toast.error("Live tracking unavailable right now"); }
    finally { setRefreshing(""); }
  };

  return (
    <div className="min-h-screen bg-[#F4F3F1]" data-testid="ccare-page">
      <header className="glass-header sticky top-0 z-40 border-b border-stone-200">
        <div className="tricolor-beam h-[3px] w-full" />
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Brand testid="ccare-brand" />
          <div className="flex items-center gap-3">
            <span className="hidden rounded-full bg-saffron/10 px-3 py-1.5 text-xs font-bold text-saffron sm:inline">Customer Care</span>
            <span className="text-sm font-semibold text-slate-600">{user?.name || user?.email}</span>
            <ChangePassword testid="ccare-change-password-btn" iconOnly
                            triggerClass="rounded-full p-2 text-slate-500 transition-colors hover:bg-stone-100 hover:text-saffron" />
            <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="ccare-logout"
                    className="rounded-full p-2 text-slate-500 transition-colors hover:bg-stone-100 hover:text-saffron" aria-label="Logout">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <h1 className="font-heading text-2xl font-bold text-navy">Daily Tracking — All Shipments</h1>
        <p className="mt-1 text-sm text-slate-500">Check every shipment's tracking and share it on WhatsApp to the sender and receiver in one click. Amounts are never shown here.</p>

        <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="ccare-filters">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input className={`${inputCls} w-64 pl-9`} placeholder="Search AWB / Order / Name…" data-testid="ccare-search"
                   value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          </div>
          <input type="date" className={inputCls} data-testid="ccare-date-from" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
          <input type="date" className={inputCls} data-testid="ccare-date-to" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
          <select className={inputCls} data-testid="ccare-status-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">All Statuses</option>
            {["Order Created", "Pickup Scheduled", "Picked Up", "In Transit", "Reached Hub", "Out for Delivery", "Delivered", "NDR", "RTO", "Cancelled"].map((s) => <option key={s}>{s}</option>)}
          </select>
          <span className="text-xs font-semibold text-slate-500" data-testid="ccare-count">{data.total} shipments</span>
        </div>

        <div className="mt-5 overflow-x-auto rounded-2xl border border-stone-200 bg-white shadow-sm">
          <table className="w-full text-left" data-testid="ccare-table">
            <thead>
              <tr className="border-b border-stone-200 bg-stone-50 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3.5">Date</th><th className="px-4 py-3.5">AWB</th><th className="px-4 py-3.5">Sender</th>
                <th className="px-4 py-3.5">Receiver</th><th className="px-4 py-3.5">Courier</th><th className="px-4 py-3.5">Status</th>
                <th className="px-4 py-3.5">Last Scan</th><th className="px-4 py-3.5">Share Tracking</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                  <Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
              )}
              {!loading && data.items.map((s) => {
                const last = (s.tracking || []).slice(-1)[0] || {};
                return (
                  <tr key={s.id} className="border-b border-stone-100 text-sm transition-colors hover:bg-saffron-light/40" data-testid={`ccare-row-${s.awb}`}>
                    <td className="whitespace-nowrap px-4 py-3.5 text-xs font-semibold text-slate-600">
                      {new Date(s.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                    <td className="px-4 py-3.5">
                      <p className="font-mono text-xs font-bold text-navy">{s.awb}</p>
                      <p className="font-mono text-[10px] text-slate-400">{s.order_id}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-xs font-bold text-navy">{s.pickup?.name || "—"}</p>
                      <p className="text-[11px] text-slate-500">{s.pickup?.city}</p>
                      <p className="font-mono text-[11px] font-semibold text-slate-600" data-testid={`ccare-sender-phone-${s.awb}`}>{s.pickup?.phone || "—"}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-xs font-bold text-navy">{s.delivery?.name || "—"}</p>
                      <p className="text-[11px] text-slate-500">{s.delivery?.city}{s.delivery?.country && s.delivery.country !== "India" ? `, ${s.delivery.country}` : ""}</p>
                      <p className="font-mono text-[11px] font-semibold text-slate-600" data-testid={`ccare-receiver-phone-${s.awb}`}>{displayPhone(s, "receiver")}</p>
                    </td>
                    <td className="px-4 py-3.5 text-xs font-semibold text-slate-600">{s.courier_name || "—"}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-3.5 text-[11px] text-slate-500">
                      {last.status ? `${last.status}${last.location ? ` · ${last.location}` : ""}` : "—"}
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <button onClick={() => refreshLive(s)} disabled={refreshing === s.awb} data-testid={`ccare-refresh-${s.awb}`}
                                title="Refresh live tracking"
                                className="grid h-8 w-8 place-items-center rounded-full border border-stone-300 text-slate-500 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-50">
                          <RefreshCw className={`h-3.5 w-3.5 ${refreshing === s.awb ? "animate-spin" : ""}`} />
                        </button>
                        {(() => { const url = carrierTrackUrl(s); return url ? (
                          <a href={url} target="_blank" rel="noreferrer" data-testid={`ccare-carrier-link-${s.awb}`}
                             title={`Open ${s.courier_name} live tracking`}
                             className="grid h-8 w-8 place-items-center rounded-full border border-express/40 bg-blue-50 text-express transition-colors hover:bg-express hover:text-white">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        ) : null; })()}
                        <button onClick={() => openPhoneEdit(s)} data-testid={`ccare-phone-edit-${s.awb}`}
                                title="Edit sender / receiver phone numbers"
                                className="grid h-8 w-8 place-items-center rounded-full border border-stone-300 text-slate-500 transition-colors hover:border-saffron hover:text-saffron">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        {s.pickup?.phone && (
                          <a href={waMsg(s, "sender")} target="_blank" rel="noreferrer" data-testid={`ccare-wa-sender-${s.awb}`}
                             className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 text-[11px] font-bold text-white transition-colors hover:bg-emerald-500">
                            <MessageCircle className="h-3.5 w-3.5" /> Sender
                          </a>
                        )}
                        {s.delivery?.phone && (
                          <a href={waMsg(s, "receiver")} target="_blank" rel="noreferrer" data-testid={`ccare-wa-receiver-${s.awb}`}
                             className="flex items-center gap-1.5 rounded-full border border-emerald-600/50 bg-emerald-50 px-3 py-1.5 text-[11px] font-bold text-emerald-700 transition-colors hover:bg-emerald-100">
                            <MessageCircle className="h-3.5 w-3.5" /> Receiver
                          </a>
                        )}
                        <div className="relative">
                          <button onClick={() => setRatingFor(ratingFor === s.awb ? null : s.awb)} data-testid={`ccare-rating-btn-${s.awb}`}
                                  title="Share Google rating link"
                                  className="grid h-8 w-8 place-items-center rounded-full border border-amber-400/60 bg-amber-50 text-amber-500 transition-colors hover:bg-amber-400 hover:text-white">
                            <Star className="h-3.5 w-3.5" />
                          </button>
                          {ratingFor === s.awb && (
                            <div className="absolute right-0 top-9 z-30 w-44 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-xl" data-testid={`ccare-rating-menu-${s.awb}`}>
                              {s.pickup?.phone && (
                                <a href={ratingMsg(s, "sender")} target="_blank" rel="noreferrer" data-testid={`ccare-rating-sender-${s.awb}`}
                                   className="block px-4 py-2.5 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-50">Rate us → Sender</a>
                              )}
                              {s.delivery?.phone && (
                                <a href={ratingMsg(s, "receiver")} target="_blank" rel="noreferrer" data-testid={`ccare-rating-receiver-${s.awb}`}
                                   className="block px-4 py-2.5 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-50">Rate us → Receiver</a>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && !data.items.length && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-slate-400" data-testid="ccare-empty">No shipments match.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {data.pages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="ccare-prev"
                    className="rounded-lg border border-stone-300 bg-white px-4 py-2 text-xs font-bold text-navy disabled:opacity-40">Prev</button>
            <span className="text-xs font-semibold text-slate-500">Page {page} / {data.pages}</span>
            <button disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="ccare-next"
                    className="rounded-lg border border-stone-300 bg-white px-4 py-2 text-xs font-bold text-navy disabled:opacity-40">Next</button>
          </div>
        )}
        {phoneEdit && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="ccare-phone-modal">
            <form onSubmit={savePhones} className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
              <h3 className="font-heading text-lg font-bold text-navy">Update Contact Numbers</h3>
              <p className="mt-1 font-mono text-xs text-slate-500">{phoneEdit.awb}</p>
              <div className="mt-4 space-y-3">
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Sender phone</label>
                  <input className={inputCls} inputMode="numeric" data-testid="ccare-phone-sender-input"
                         value={phoneForm.pickup_phone}
                         onChange={(e) => setPhoneForm({ ...phoneForm, pickup_phone: e.target.value })}
                         placeholder="10-digit mobile" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Receiver phone</label>
                  <input className={inputCls} inputMode="numeric" data-testid="ccare-phone-receiver-input"
                         value={phoneForm.delivery_phone}
                         onChange={(e) => setPhoneForm({ ...phoneForm, delivery_phone: e.target.value })}
                         placeholder="10-digit mobile" />
                </div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" onClick={() => setPhoneEdit(null)} data-testid="ccare-phone-cancel"
                        className="rounded-lg border border-stone-300 px-4 py-2 text-sm font-bold text-slate-600">Cancel</button>
                <button type="submit" disabled={savingPhone} data-testid="ccare-phone-save-btn"
                        className="flex items-center gap-2 rounded-lg bg-saffron px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-saffron-hover disabled:opacity-60">
                  {savingPhone && <Loader2 className="h-4 w-4 animate-spin" />} Save Numbers
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
